import { useState, useCallback, useMemo } from 'react'
import { useLazyQuery } from '@apollo/client'

import { GET_EMPLOYEES } from 'src/graphql/employees.graphql'
import { toast } from 'src/components/Overlays/Toast/Toast'

import { FilterOption } from '../types'

interface UseRecruitersFilterProps {
  selectedRecruiters: string[]
  onRecruitersChange: (recruiters: string[]) => void
  /**
   * Optional role type filter. When omitted, loads all company employees.
   */
  roleTypes?: string[]
}

export const useRecruitersFilter = ({
  selectedRecruiters,
  onRecruitersChange,
  roleTypes,
}: UseRecruitersFilterProps) => {
  const [options, setOptions] = useState<FilterOption[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  const onError = useCallback(() => {
    toast('Failed to load employees', 'error')
  }, [])

  const [loadRecruiters, { loading }] = useLazyQuery(GET_EMPLOYEES, {
    variables: {
      filters: roleTypes && roleTypes.length > 0 ? { roleTypes } : {},
      pageInfo: { offset: 0, limit: 100 },
    },
    onCompleted: (data) => {
      const recruiterOptions =
        data?.employees?.items?.map((employee) => ({
          label: `${employee.firstName} ${employee.lastName}`,
          value: employee.employeeId,
        })) || []
      setOptions(recruiterOptions)
      setIsLoaded(true)
    },
    onError,
  })

  // Update options to reflect selected state
  const optionsWithSelection = useMemo(() => {
    return options.map((option) => ({
      ...option,
      selected: selectedRecruiters.includes(option.value),
    }))
  }, [options, selectedRecruiters])

  const loadData = useCallback(() => {
    if (!isLoaded && !loading) {
      loadRecruiters()
    }
  }, [isLoaded, loading, loadRecruiters])

  return {
    options: optionsWithSelection,
    selected: selectedRecruiters,
    onChange: onRecruitersChange,
    loadData,
    loading,
    isLoaded,
  }
}
